import { useState, useEffect } from "react";

const PropertyStepProgress = ({
  currentStep,
  steps = [], // Array of { id, label, action, validation }
  variant = "create", // "create" | "edit"
  handlePrevStep, // only for create mode
}) => {
  const [animatedStep, setAnimatedStep] = useState(currentStep);

  useEffect(() => {
    if (animatedStep === currentStep) return;
    const timer = setTimeout(() => setAnimatedStep(currentStep), 50);
    return () => clearTimeout(timer);
  }, [currentStep]);

  // ✅ Handles step click with validation logic
  const handleStepClick = (step, index) => {
    const currentStepIndex = steps.findIndex((s) => s.id === currentStep);

    // Allow going backward
    if (index <= currentStepIndex) {
      step.action();
      return;
    }

    // Validate all previous steps
    for (let i = 0; i < index; i++) {
      if (steps[i].validation && !steps[i].validation()) {
        return; // Stop moving forward if validation fails
      }
    }

    step.action();
  };

  /** ------------------ UNIVERSAL RENDER ------------------ **/
  const renderSteps = () => {
    if (variant === "edit") {
      return (
        <div className="flex items-center justify-start mb-5">
          <div className="flex flex-wrap w-full -mb-px text-sm font-medium text-center text-gray-500">
            {steps.map((step, index) => (
              <div
                key={step.id}
                onClick={() => handleStepClick(step, index)}
                className="me-2 cursor-pointer"
              >
                <div
                  className={`inline-block text-base p-4 rounded-t-lg transition-all duration-300 ${
                    currentStep === step.id
                      ? "text-customGreen border-b-2 font-medium border-customGreen"
                      : "text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300"
                  }`}
                >
                  {step.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    // Create Mode: Progress Bar Style
    return (
      <div className="flex items-center justify-center mb-8 mt-2">
        <div className="relative flex items-center justify-between w-full max-w-2xl">
          {steps.map((step, index) => {
            const currentStepIndex = steps.findIndex((s) => s.id === currentStep);
            const isActive = currentStep === step.id;
            const isCompleted = index < currentStepIndex;

            return (
              <div
                key={step.id}
                onClick={() => handleStepClick(step, index)}
                className="flex flex-col items-center w-1/3 cursor-pointer z-10"
              >
                {/* Circle */}
                <div
                  className={`w-9 h-9 border-2 rounded-full flex items-center justify-center font-bold mb-2 transition-all duration-500 transform
                  ${isCompleted ? "bg-white border-customGreen" : ""}
                  ${isActive ? "bg-white border-customGreen" : ""}
                  ${!isActive && !isCompleted ? "bg-gray-300 border-gray-300" : ""}
                `}
                >
                  <div
                    className={`h-6 w-6 rounded-full flex items-center justify-center text-[14px] transition-all duration-500
                  ${isCompleted ? "bg-customGreen text-white" : ""}
                  ${isActive ? "bg-customGreen text-white" : ""}
                  ${!isActive && !isCompleted ? "text-gray-700" : ""}
                `}
                  >
                    {isCompleted ? "✓" : ""}
                  </div>
                </div>

                {/* Label */}
                <span
                  className={`text-sm font-medium text-center transition-all duration-300 ${
                    isActive ? "text-customGreen" : ""
                  }`}
                >
                  {step.label}
                </span>
              </div>
            );
          })}

          {/* Connector Line */}
          <div className="absolute top-4 left-[16%] w-[68%] h-0.5 bg-gray-300 z-0">
            <div
              className="h-0.5 bg-customGreen transition-all duration-500 ease-in-out"
              style={{
                width: `${
                  (steps.findIndex((s) => s.id === currentStep) /
                    (steps.length - 1)) *
                  100
                }%`,
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style jsx global>{`
        @keyframes fadeIn {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-in-out;
        }
      `}</style>
      {renderSteps()}
    </>
  );
};

export default PropertyStepProgress;
